import React, { useEffect, useRef } from "react";
import * as THREE from "three";

type HeroThreeSceneProps = {
  className?: string;
};

export function HeroThreeScene({ className }: HeroThreeSceneProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(48, 1, 0.1, 100);
    camera.position.z = 3.35;

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    const geometry = new THREE.IcosahedronGeometry(1.05, 1);
    const material = new THREE.MeshStandardMaterial({
      color: 0x6366f1,
      emissive: 0x1e1b4b,
      emissiveIntensity: 0.35,
      metalness: 0.45,
      roughness: 0.38,
      wireframe: true,
    });
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    const inner = new THREE.Mesh(
      new THREE.IcosahedronGeometry(0.52, 0),
      new THREE.MeshStandardMaterial({
        color: 0x22d3ee,
        metalness: 0.2,
        roughness: 0.55,
        transparent: true,
        opacity: 0.22,
      }),
    );
    scene.add(inner);

    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const key = new THREE.DirectionalLight(0xa5b4fc, 0.9);
    key.position.set(2.2, 2.4, 3);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x22d3ee, 0.45);
    rim.position.set(-2.5, -1, 2);
    scene.add(rim);

    host.appendChild(renderer.domElement);

    const setSize = () => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      if (w === 0 || h === 0) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };

    setSize();
    const ro = new ResizeObserver(setSize);
    ro.observe(host);

    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (!reducedMotion) {
        mesh.rotation.y += 0.0055;
        mesh.rotation.x += 0.0028;
        inner.rotation.y -= 0.004;
        inner.rotation.x -= 0.002;
      }
      renderer.render(scene, camera);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      geometry.dispose();
      inner.geometry.dispose();
      (inner.material as THREE.Material).dispose();
      material.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === host) {
        host.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={hostRef} className={className} aria-hidden />;
}
